"use client"

import { usePostChatbotJoin } from "openapi/api/endpoints"
import type { Event, PostChatbotJoinBody } from "openapi/api/model"
import type { CommentLabel } from "@/lib/comment-labels"

type JoinEventChatbotArgs = {
  event: Event
  meetingUrl?: string
}

const isNonEmptyString = (value?: string | null): value is string =>
  typeof value === "string" && value.trim().length > 0

const isValidUrl = (value?: string | null): value is string => {
  if (!isNonEmptyString(value)) return false

  try {
    new URL(value)
    return true
  } catch {
    return false
  }
}

export const resolveEventLinkType = (event: Event): "candidate" | "job" =>
  event.candidate_id ? "candidate" : "job"

export const mapEventTypeToCommentLabel = (eventType?: string | null): CommentLabel => {
  const normalized = eventType?.trim().toLowerCase() ?? ""

  if (normalized === "meeting") return "meeting"
  if (normalized === "screening") return "internal_screening"
  if (normalized === "interview" || normalized === "interviewing") return "client_interview_notes"
  if (normalized === "email") return "email"
  if (normalized === "call") return "call"
  if (normalized === "phone_call" || normalized === "phone screen" || normalized === "phone_screen") {
    return "phone_call"
  }
  if (normalized === "text_message" || normalized === "text" || normalized === "sms") {
    return "text_message"
  }

  return "general_update"
}

export const resolveEventMeetingUrl = (event: Event, overrideMeetingUrl?: string): string => {
  if (isNonEmptyString(overrideMeetingUrl)) {
    if (!isValidUrl(overrideMeetingUrl)) {
      throw new Error("The provided meeting URL is invalid.")
    }

    return overrideMeetingUrl.trim()
  }

  const metadataMeetingUrl = event.provider_meeting_metadata?.meeting_url
  if (isValidUrl(metadataMeetingUrl)) return metadataMeetingUrl.trim()

  const metadataWebLink = event.provider_meeting_metadata?.web_link
  if (isValidUrl(metadataWebLink)) return metadataWebLink.trim()

  if (isValidUrl(event.meeting_url)) return event.meeting_url.trim()
  if (isValidUrl(event.location)) return event.location.trim()

  throw new Error("This event does not have a valid meeting URL.")
}

export const resolveEventChatbotJoinPayload = ({
  event,
  meetingUrl,
}: JoinEventChatbotArgs): PostChatbotJoinBody => {
  const resolvedMeetingUrl = resolveEventMeetingUrl(event, meetingUrl)
  const resolvedLabel = mapEventTypeToCommentLabel(event.event_type)

  if (resolveEventLinkType(event) === "candidate" && event.candidate_id) {
    // Candidate-linked events prefer job_candidate context when a job is also attached.
    if (event.job_id) {
      return {
        label: resolvedLabel,
        entity_type: "job_candidate",
        job_id: event.job_id,
        candidate_id: event.candidate_id,
        meeting_url: resolvedMeetingUrl,
      }
    }

    return {
      label: resolvedLabel,
      entity_type: "candidate",
      entity_id: event.candidate_id,
      meeting_url: resolvedMeetingUrl,
    }
  }

  if (event.job_id) {
    return {
      label: resolvedLabel,
      entity_type: "jobs",
      entity_id: event.job_id,
      meeting_url: resolvedMeetingUrl,
    }
  }

  throw new Error("This event must be linked to a job or candidate before a bot can join.")
}

export function useEventChatbotJoin() {
  const mutation = usePostChatbotJoin()

  const joinEvent = async (args: JoinEventChatbotArgs) => {
    const payload = resolveEventChatbotJoinPayload(args)
    return mutation.mutateAsync({ data: payload })
  }

  return {
    ...mutation,
    joinEvent,
    resolveEventChatbotJoinPayload,
    resolveEventMeetingUrl,
    resolveEventLinkType,
    mapEventTypeToCommentLabel,
  }
}
